/**
 * Which sidebar entries and module tabs a user may see.
 *
 * Presentation only: the API keeps enforcing every key, and a direct URL still
 * lands on the page's own «دسترسی ندارید» panel. The rule is "show an entry if
 * the user holds ANY key for it, read or write", so a user with no key at all
 * for a section does not see its tab.
 *
 * A rule is a list of alternatives; an alternative is a list of items that must
 * ALL be held. An item ending in `.` is an entity prefix (`crm.lead.` matches
 * `crm.lead.read`, `crm.lead.create`, …), anything else is an exact key. Keys
 * come from `GET /me` `capabilities.allowedKeys` (evaluated DENY-first on the
 * server).
 */

export type AccessItem = string;
export type AccessRule = readonly (readonly AccessItem[])[];

export interface AccessContext {
  /** False until the signed-in user's profile has loaded: nothing is hidden before then. */
  readonly ready: boolean;
  readonly allowedKeys: readonly string[];
  readonly roleKeys: readonly string[];
}

/**
 * The built-in tenant administrator sees every tab. Deliberately the role key
 * only — `capabilities.isTenantAdmin` also matches a custom role whose display
 * name merely contains «مدیر»/«admin», which would defeat the hiding.
 */
export const TENANT_ADMIN_ROLE_KEY = 'tenant-admin';

const any = (...items: readonly AccessItem[]): AccessRule => items.map((item) => [item]);
const all = (...items: readonly AccessItem[]): AccessRule => [items];

function holds(keys: readonly string[], item: AccessItem): boolean {
  return item.endsWith('.') ? keys.some((key) => key.startsWith(item)) : keys.includes(item);
}

export function ruleAllows(rule: AccessRule, keys: readonly string[]): boolean {
  return rule.some((alternative) => alternative.every((item) => holds(keys, item)));
}

/** `undefined` means "always visible". */
export function canSee(rule: AccessRule | undefined, context: AccessContext): boolean {
  if (rule === undefined || !context.ready) return true;
  if (context.roleKeys.includes(TENANT_ADMIN_ROLE_KEY)) return true;
  return ruleAllows(rule, context.allowedKeys);
}

const AI: AccessRule = any('ai.');

/** Tab rules per module, keyed by the module's tab ids. A tab with no entry is always visible. */
export const TAB_ACCESS: Readonly<Record<string, Readonly<Record<string, AccessRule>>>> = {
  crm: {
    overview: any('crm.pipeline.', 'crm.activity.', 'sales.order.'),
    leads: any('crm.lead.'),
    customers: any('master.party.'),
    activities: any('crm.activity.'),
    quotes: any('sales.quote.'),
    orders: any('sales.order.'),
    invoices: all('sales.invoice.', 'accounting.ar-open-item.'),
    returns: any('sales.return.'),
    'my-sales': any('sales.commission.'),
    team: any('sales.team.', 'sales.commission-rule.', 'sales.commission.'),
    'call-base': all('crm.communication.', 'master.party.'),
    assistant: AI,
  },
  inventory: {
    overview: any('inventory.'),
    stock: any('inventory.stock.'),
    cardex: any('inventory.stock.'),
    'count-sessions': any('inventory.stock.', 'inventory.adjustment.'),
    reconciliation: any('inventory.reconciliation.', 'inventory.stock.'),
    reservations: any('inventory.reservation.', 'inventory.stock.'),
    receipts: any('inventory.movement.', 'inventory.receipt.'),
    issues: any('inventory.movement.', 'inventory.issue.'),
    transfers: any('inventory.movement.', 'inventory.transfer.'),
    adjustments: any('inventory.movement.', 'inventory.adjustment.'),
    'tracking-profiles': any('inventory.stock.'),
    lots: any('inventory.stock.', 'inventory.lot.'),
    serials: any('inventory.stock.'),
    'uom-conversions': any('inventory.stock.', 'inventory.adjustment.'),
    reports: any('inventory.stock.'),
    assistant: AI,
  },
  procurement: {
    overview: any('procurement.'),
    requests: any('procurement.request.'),
    orders: any('procurement.order.'),
    suppliers: any('master.party.'),
    assistant: AI,
  },
  finance: {
    overview: any('accounting.'),
    journals: any('accounting.journal-entry.'),
    accounts: any('accounting.coa.'),
    periods: any('accounting.fiscal-period.', 'accounting.journal-entry.'),
    ar: any('accounting.ar-open-item.'),
    ap: any('accounting.ap-open-item.'),
    treasury: any('accounting.treasury-account.', 'accounting.treasury-transaction.'),
    cheques: any('accounting.cheque.'),
    'bank-recon': any('accounting.bank-reconciliation.'),
    'sales-accounts': any('sales.posting-account.'),
    vat: any('accounting.vat-entry.'),
    modian: any('accounting.moadian.'),
    dimensions: any('master.dimension.', 'master.dimension-value.'),
    'year-end': any('accounting.fiscal-period.'),
    reports: any('accounting.journal-entry.', 'accounting.cross-book-report.'),
    books: any('accounting.book.'),
    assistant: AI,
  },
  projects: {
    overview: any('projects.'),
    list: any('projects.project.'),
    tasks: any('projects.task.'),
    todos: any('projects.todo.'),
    reminders: any('projects.reminder.'),
    overdue: any('projects.project.', 'projects.task.'),
    comments: any('projects.comment.'),
    assistant: AI,
  },
  'master-data': {
    overview: any('master.'),
    party: any('master.party.'),
    product: any('master.product.'),
    uom: any('master.uom.'),
    tax: any('master.tax.'),
    warehouse: any('master.warehouse.'),
    location: any('master.location.'),
    'brand-assets': any('master.brand-asset.'),
  },
};

/** A module is visible when any of its tabs is; the assistant tab alone does not make it so. */
export function moduleAccess(moduleId: string): AccessRule {
  const tabs = TAB_ACCESS[moduleId] ?? {};
  return Object.entries(tabs)
    .filter(([tabId]) => tabId !== 'assistant')
    .flatMap(([, rule]) => rule);
}

/**
 * Sidebar / bottom-nav entries by `NavEntry.id`. Missing means always visible
 * (home, the self-service security page, …).
 */
export const NAV_ACCESS: Readonly<Record<string, AccessRule>> = {
  ai: AI,
  crm: moduleAccess('crm'),
  inventory: moduleAccess('inventory'),
  procurement: moduleAccess('procurement'),
  finance: moduleAccess('finance'),
  projects: moduleAccess('projects'),
  'master-data': moduleAccess('master-data'),
  'users-access': any('tenant.user.manage', 'tenant.role.manage'),
  'data-import': any(
    'master.party.import',
    'master.product.import',
    'master.uom.import',
    'accounting.coa-account.import',
    'crm.lead.import',
  ),
  subscription: any('tenant.subscription.manage'),
  tasks: moduleAccess('projects'),
};

export function canSeeNavEntry(entryId: string, context: AccessContext): boolean {
  return canSee(NAV_ACCESS[entryId], context);
}

export function canSeeTab(moduleId: string, tabId: string, context: AccessContext): boolean {
  return canSee(TAB_ACCESS[moduleId]?.[tabId], context);
}
